import {
  appThemeApiRef,
  identityApiRef,
  useApi,
} from '@backstage/core-plugin-api';
import AccountCircleIcon from '@mui/icons-material/AccountCircleOutlined';
import MenuOpenOutlinedIcon from '@mui/icons-material/MenuOpenOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import Popover from '@mui/material/Popover';
import Switch from '@mui/material/Switch';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { makeStyles } from 'tss-react/mui';
import { MoonIcon } from '../icons/MoonIcon';
import { getColors } from '../tokens/getColors';
import { fontFamilyCorpoSText } from '../tokens/typography';
import { useSidebar } from './SidebarContext';

type ThemeOption = 'light' | 'dark' | 'auto';

const THEME_OPTIONS: {
  id: ThemeOption;
  label: string;
  themeId: string | undefined;
}[] = [
  { id: 'light', label: 'Light', themeId: 'odp-v2-light' },
  { id: 'dark', label: 'Dark', themeId: 'odp-v2-dark' },
  { id: 'auto', label: 'Auto', themeId: undefined },
];

function themeIdToOption(id: string | undefined): ThemeOption {
  const match = THEME_OPTIONS.find(o => o.themeId === id);
  return match ? match.id : 'auto';
}

const useStyles = makeStyles()(theme => {
  const c = getColors(theme.palette.mode);
  const isLight = theme.palette.mode === 'light';
  const fgAlpha = (a: number) =>
    isLight ? `rgba(0, 0, 0, ${a})` : `rgba(255, 255, 255, ${a})`;
  return {
    avatarButton: {
      width: 32,
      height: 32,
      minWidth: 32,
      borderRadius: '50%',
      backgroundColor: isLight ? '#6B6B6B' : '#4A4A4A',
      color: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: 12,
      fontWeight: 600,
      lineHeight: 1,
      fontFamily: fontFamilyCorpoSText,
      cursor: 'pointer',
      border: 'none',
      padding: 0,
      boxSizing: 'border-box' as const,
      transition: 'box-shadow 0.15s ease',
      '&:hover': {
        boxShadow: `0 0 0 2px ${fgAlpha(0.25)}`,
      },
    },
    popover: {
      '& .MuiPaper-root': {
        backgroundColor: isLight ? '#FFFFFF' : '#1A1A1A',
        border: `1px solid ${fgAlpha(0.1)}`,
        borderRadius: 12,
        minWidth: 280,
        padding: 0,
        boxShadow: isLight
          ? '0 8px 32px rgba(0, 0, 0, 0.15)'
          : '0 8px 32px rgba(0, 0, 0, 0.5)',
      },
    },
    userInfo: {
      padding: '16px 16px 12px',
    },
    userName: {
      fontFamily: fontFamilyCorpoSText,
      fontSize: 14,
      fontWeight: 600,
      color: c.text.primary,
      margin: 0,
      lineHeight: 1.4,
    },
    userEmail: {
      fontFamily: fontFamilyCorpoSText,
      fontSize: 12,
      color: c.text.secondary,
      margin: '2px 0 0',
      lineHeight: 1.4,
    },
    divider: {
      height: 1,
      background: fgAlpha(0.08),
      margin: '0 12px',
    },
    menuItem: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      width: '100%',
      padding: '10px 16px',
      background: 'none',
      border: 'none',
      color: fgAlpha(0.8),
      fontFamily: fontFamilyCorpoSText,
      fontSize: 13,
      cursor: 'pointer',
      transition: 'background 0.12s ease',
      '& svg': { width: 18, height: 18, flexShrink: 0 },
      '&:hover': {
        background: fgAlpha(0.06),
      },
    },
    menuRow: {
      display: 'flex',
      alignItems: 'center',
      gap: 12,
      padding: '8px 16px',
      color: fgAlpha(0.8),
      fontFamily: fontFamilyCorpoSText,
      fontSize: 13,
      '& svg': { width: 18, height: 18, flexShrink: 0 },
    },
    rowLabel: {
      flex: 1,
    },
    segmentedControl: {
      display: 'flex',
      borderRadius: 6,
      overflow: 'hidden',
      border: `1px solid ${fgAlpha(0.12)}`,
    },
    segmentOption: {
      padding: '4px 10px',
      fontSize: 11,
      fontWeight: 500,
      fontFamily: fontFamilyCorpoSText,
      border: 'none',
      cursor: 'pointer',
      color: fgAlpha(0.6),
      background: 'transparent',
      transition: 'background 0.12s ease, color 0.12s ease',
      '&:hover': {
        color: fgAlpha(0.85),
      },
    },
    segmentOptionActive: {
      // Use && to double specificity so this always wins over segmentOption,
      // regardless of CSS injection order (e.g. when switching themes).
      '&&': {
        background: c.primary.main,
        color: '#FFFFFF',
      },
    },
    sidebarSwitch: {
      '& .MuiSwitch-switchBase': {
        color: fgAlpha(0.3),
        '&.Mui-checked': {
          color: c.primary.main,
        },
        '&.Mui-checked + .MuiSwitch-track': {
          backgroundColor: c.primary.main,
          opacity: 0.5,
        },
      },
      '& .MuiSwitch-track': {
        backgroundColor: fgAlpha(0.2),
      },
    },
    bottomSection: {
      padding: '4px 0 8px',
    },
    classicLink: {
      display: 'block',
      padding: '4px 16px 4px 50px',
      background: 'none',
      border: 'none',
      color: fgAlpha(0.45),
      fontFamily: fontFamilyCorpoSText,
      fontSize: 11,
      cursor: 'pointer',
      textAlign: 'left' as const,
      '&:hover': {
        color: fgAlpha(0.75),
        textDecoration: 'underline',
      },
    },
    menuSection: {
      padding: '4px 0',
    },
  };
});

function getInitials(name: string | undefined): string {
  if (!name) return 'MB';

  let s = name.trim().replace(/\s+/g, ' ');

  if (/,/.test(s)) {
    // Split on first comma
    const [beforeComma, afterComma] = s.split(/,(.+)/).map(p => p.trim());
    if (afterComma) {
      // Remove any parenthetical trailing parts from afterComma (e.g. "(234)")
      const cleanedAfter = afterComma.replace(/\(.*?\)/g, '').trim();
      s = `${cleanedAfter} ${beforeComma}`.trim();
    }
  }

  // Remove any remaining parenthetical groups anywhere
  s = s.replace(/\(.*?\)/g, '').trim();

  const parts = s.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    const first = parts[0][0];
    const last = parts[parts.length - 1][0];
    return (first + last).toUpperCase();
  }

  const single = parts[0]?.[0] ?? 'U';
  return single.toUpperCase();
}

export const UserMenu = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const appThemeApi = useApi(appThemeApiRef);
  const identityApi = useApi(identityApiRef);
  const { collapsed, toggle: toggleSidebar } = useSidebar();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [displayName, setDisplayName] = useState<string | undefined>();
  const [email, setEmail] = useState<string | undefined>();
  const [themeId, setThemeId] = useState<string | undefined>(() =>
    appThemeApi.getActiveThemeId(),
  );

  useEffect(() => {
    const subscription = appThemeApi.activeThemeId$().subscribe(id => {
      setThemeId(prev => (prev === id ? prev : id));
    });
    return () => subscription.unsubscribe();
  }, [appThemeApi]);

  const activeTheme = themeIdToOption(themeId);

  useEffect(() => {
    identityApi
      .getProfileInfo()
      .then(profile => {
        setDisplayName(profile.displayName);
        setEmail(profile.email);
      })
      .catch(() => {});
  }, [identityApi]);

  const handleThemeChange = useCallback(
    (option: ThemeOption) => {
      const entry = THEME_OPTIONS.find(o => o.id === option)!;
      appThemeApi.setActiveThemeId(entry.themeId);
    },
    [appThemeApi],
  );

  const open = Boolean(anchorEl);
  const initials = getInitials(displayName);

  return (
    <>
      {/* eslint-disable-next-line react/forbid-elements */}
      <button
        type="button"
        className={classes.avatarButton}
        onClick={e => setAnchorEl(e.currentTarget)}
        aria-label="User menu"
        aria-haspopup="true"
        aria-expanded={open}
      >
        {initials}
      </button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        className={classes.popover}
        slotProps={{ paper: { elevation: 0 } }}
      >
        {/* User info */}
        <div className={classes.userInfo}>
          <p className={classes.userName}>{displayName || 'User'}</p>
          {email && <p className={classes.userEmail}>{email}</p>}
        </div>

        <div className={classes.divider} />

        {/* Navigation items */}
        <div className={classes.menuSection}>
          <button
            type="button"
            className={classes.menuItem}
            onClick={() => {
              setAnchorEl(null);
              navigate('/settings');
            }}
          >
            <AccountCircleIcon />
            Profile
          </button>
          <button
            type="button"
            className={classes.menuItem}
            onClick={() => {
              setAnchorEl(null);
              navigate('/settings');
            }}
          >
            <SettingsOutlinedIcon />
            Settings
          </button>
        </div>

        <div className={classes.divider} />

        {/* Theme & sidebar */}
        <div className={classes.bottomSection}>
          <div className={classes.menuRow}>
            <MoonIcon />
            <span className={classes.rowLabel}>Theme</span>
            <div className={classes.segmentedControl}>
              {THEME_OPTIONS.map(option => (
                <button
                  key={option.id}
                  type="button"
                  className={[
                    classes.segmentOption,
                    activeTheme === option.id && classes.segmentOptionActive,
                  ]
                    .filter(Boolean)
                    .join(' ')}
                  onClick={() => handleThemeChange(option.id)}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
          <button
            type="button"
            className={classes.classicLink}
            onClick={() => {
              appThemeApi.setActiveThemeId('backstage-theme-light');
              setAnchorEl(null);
              navigate('/home-classic');
            }}
          >
            Switch to classic theme
          </button>

          {/* Pin sidebar */}
          <div className={classes.menuRow}>
            <MenuOpenOutlinedIcon />
            <span className={classes.rowLabel}>Pin Sidebar</span>
            <Switch
              size="small"
              checked={!collapsed}
              onChange={toggleSidebar}
              className={classes.sidebarSwitch}
            />
          </div>
        </div>
      </Popover>
    </>
  );
};
